import { useI18n } from "@/context/I18nContext";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/common/EmptyState";
import { fmtInt, fmtNumber } from "@/lib/format";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

/** Small titled card wrapper used by every POS-insights panel. */
export function PosCard({ icon: Icon, title, desc, testId, right, children, className = "" }) {
    return (
        <Card className={`rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)] overflow-hidden ${className}`}>
            <CardContent className="p-5">
                <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                        {Icon ? (
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[color:var(--brand-orange-soft)]">
                                <Icon className="h-4 w-4 text-[color:var(--brand-orange)]" />
                            </span>
                        ) : null}
                        <div>
                            <h3 className="text-sm font-bold text-[color:var(--ink)]">{title}</h3>
                            {desc ? <p className="text-[11px] text-[color:var(--ink-muted)]">{desc}</p> : null}
                        </div>
                    </div>
                    {right}
                </div>
                <div data-testid={testId}>{children}</div>
            </CardContent>
        </Card>
    );
}

/** Ranked table with a share bar on the first numeric column. */
export function RankTable({ testId, rows, columns, rowTestId, shareKey, emptyLabel, dataset }) {
    const { lang, t } = useI18n();
    const total = shareKey ? rows.reduce((s, r) => s + (Number(r[shareKey]) || 0), 0) : 0;

    if (!rows?.length) {
        return (
            <EmptyState
                compact
                testId={testId}
                title={emptyLabel || t("no_data")}
                dataset={dataset || "pos_branch_daily"}
            />
        );
    }
    const cell = (r, c) => {
        const v = r[c.key];
        if (v === null || v === undefined || v === "") return "\u2014";
        if (c.type === "int") return fmtInt(v, lang);
        if (c.type === "num") return fmtNumber(v, lang, 2);
        if (c.type === "num1") return fmtNumber(v, lang, 1);
        return v;
    };
    return (
        <div data-testid={testId} className="overflow-x-auto">
            <Table>
                <TableHeader>
                    <TableRow className="hover:bg-transparent">
                        {columns.map((c) => (
                            <TableHead key={c.key}
                                className={`text-[color:var(--ink-muted)] ${c.type ? "text-end" : "text-start"}`}>
                                {c.label}
                            </TableHead>
                        ))}
                        {shareKey ? <TableHead className="text-end text-[color:var(--ink-muted)]">{t("pi_col_share")}</TableHead> : null}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={rowTestId ? rowTestId(r, i) : i}
                            data-testid={rowTestId ? rowTestId(r, i) : undefined}>
                            {columns.map((c) => (
                                <TableCell key={c.key}
                                    className={`${c.type ? "text-end tnum" : "text-start"} ${c.strong ? "font-semibold text-[color:var(--ink)]" : "text-[color:var(--ink)]"}`}>
                                    <bdi dir={c.type ? "ltr" : "auto"}>{cell(r, c)}</bdi>
                                </TableCell>
                            ))}
                            {shareKey ? (
                                <TableCell className="text-end">
                                    <div className="flex items-center justify-end gap-2">
                                        <span className="tnum text-xs text-[color:var(--ink-muted)]">
                                            <bdi dir="ltr">{total ? fmtNumber(((Number(r[shareKey]) || 0) / total) * 100, lang, 1) : "0.0"}%</bdi>
                                        </span>
                                        <span className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-[color:var(--bg-surface-2)] sm:block">
                                            <span className="block h-full rounded-full bg-[color:var(--brand-orange)]"
                                                style={{ width: `${total ? Math.min(100, ((Number(r[shareKey]) || 0) / total) * 100) : 0}%` }} />
                                        </span>
                                    </div>
                                </TableCell>
                            ) : null}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
